import { useState } from "react";
import { cn } from "@/lib/utils";
import { WeightedYesNoQuestion } from "./WeightedYesNoQuestion";
import { WeightedMultipleChoiceQuestion, type ChoiceOption } from "./WeightedMultipleChoiceQuestion";
import { MultiSubFieldQuestion, type SubField } from "./MultiSubFieldQuestion";
import { OpenEndedQuestion } from "./OpenEndedQuestion";
import { useTranslation } from "@/hooks/useTranslation";
import { pickLocalized } from "@/lib/localized";

// ─────────────────────────────────────────────
// QuestionCard
//
// Renders a single question with its appropriate control per QuestionType.
// Includes PM comment textarea and answered indicator.
// Design: §6.3 Domain Tab Content — Question Card
// ─────────────────────────────────────────────

export type QuestionType =
  | "WEIGHTED_YES_NO"
  | "WEIGHTED_MULTIPLE_CHOICE"
  | "MULTI_SUB_FIELD"
  | "OPEN_ENDED";

export interface QuestionOptions {
  // WEIGHTED_YES_NO
  yes?: { score: number } | number;
  no?: { score: number } | number;
  partially?: { score: number } | number;
  // WEIGHTED_MULTIPLE_CHOICE
  choices?: ChoiceOption[];
  // MULTI_SUB_FIELD
  subFields?: SubField[];
  // Raw array format for WEIGHTED_MULTIPLE_CHOICE
  [key: string]: unknown;
}

export interface QuestionCardProps {
  questionId: string;
  index: number;
  code: string;
  text: string;
  textSr?: string;
  helpText?: string;
  helpTextSr?: string;
  type: QuestionType;
  options: QuestionOptions | null;
  value: unknown;
  comment: string | null;
  readOnly?: boolean;
  onValueChange: (questionId: string, value: unknown) => void;
  onCommentChange: (questionId: string, comment: string) => void;
  domainLabel?: string;
}

export function parseChoices(options: QuestionOptions | null): ChoiceOption[] {
  if (!options) return [];
  // Object format: { choices: [...] }
  if (Array.isArray(options.choices)) return options.choices as ChoiceOption[];
  // Array format (IMPL-031 dual format)
  if (Array.isArray(options)) {
    return (options as Array<{ key?: string; value?: string; label?: string; score?: number }>).map(
      (o) => ({
        value: o.key ?? o.value ?? "",
        label: o.label ?? o.key ?? "",
        labelSr: (o as { labelSr?: string }).labelSr,
        score: o.score ?? 0,
      })
    );
  }
  return [];
}

export function parseSubFields(options: QuestionOptions | null): SubField[] {
  if (!options) return [];
  if (!Array.isArray(options.subFields)) return [];
  // Normalise: DB stores sub-fields with { key, label, options:[{key,label,score}] }
  // but the SubField interface expects { name, options:[{value,label,score}] }.
  return (options.subFields as unknown as Array<Record<string, unknown>>).map((sf) => ({
    name: (sf.name as string | undefined) ?? (sf.label as string | undefined) ?? (sf.key as string | undefined) ?? "",
    nameSr: sf.nameSr as string | undefined,
    options: Array.isArray(sf.options)
      ? (sf.options as Array<Record<string, unknown>>).map((opt) => ({
          value: (opt.value as string | undefined) ?? (opt.key as string | undefined) ?? "",
          label: (opt.label as string | undefined) ?? (opt.key as string | undefined) ?? "",
          labelSr: opt.labelSr as string | undefined,
          score: (opt.score as number | undefined) ?? 0,
        }))
      : [],
  }));
}

export function isAnswered(type: QuestionType, value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (type === "MULTI_SUB_FIELD") {
    const v = value as Record<string, string>;
    return Object.keys(v).length > 0 && Object.values(v).some((x) => x !== "");
  }
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

export function QuestionCard({
  questionId,
  index,
  code: _code,
  text,
  textSr,
  helpText,
  helpTextSr,
  type,
  options,
  value,
  comment,
  readOnly,
  onValueChange,
  onCommentChange,
  domainLabel,
}: QuestionCardProps) {
  const { t, language } = useTranslation();
  const [showComment, setShowComment] = useState(
    comment !== null && comment !== undefined && comment !== ""
  );

  const answered = isAnswered(type, value);

  return (
    <div
      className={cn(
        "w-full rounded-card border bg-card p-5 transition-colors",
        answered
          ? "border-border-default"
          : "border-border-default hover:border-border-default"
      )}
    >
      {/* Question header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {/* Question number */}
          <span
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-semibold"
            style={{
              backgroundColor: answered ? "var(--accent-bg)" : "var(--bg-hover)",
              color: answered ? "var(--accent-text)" : "var(--text-muted)",
            }}
          >
            {index}
          </span>
          <div className="flex flex-col gap-1 min-w-0">
            <p className="text-[14px] font-medium text-text-primary">{pickLocalized(text, textSr, language)}</p>
            {helpText && (
              <p className="text-[13px] text-text-muted">{pickLocalized(helpText, helpTextSr, language)}</p>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {domainLabel && (
            <span
              className="rounded-badge px-2 py-0.5 text-[11px] font-semibold"
              style={{
                backgroundColor: "var(--accent-bg)",
                color: "var(--accent-text)",
              }}
            >
              {domainLabel}
            </span>
          )}
          {answered && (
            <span
              className="rounded-badge px-2 py-0.5 text-[11px] font-semibold"
              style={{
                backgroundColor: "var(--success-bg)",
                color: "var(--success)",
              }}
            >
              {t("assessment.answered")}
            </span>
          )}
        </div>
      </div>

      {/* Question control */}
      <div className="mb-4 pl-9">
        {type === "WEIGHTED_YES_NO" && (
          <WeightedYesNoQuestion
            questionId={questionId}
            value={typeof value === "string" ? value : null}
            onChange={(v) => onValueChange(questionId, v)}
            readOnly={readOnly}
          />
        )}
        {type === "WEIGHTED_MULTIPLE_CHOICE" && (
          <WeightedMultipleChoiceQuestion
            questionId={questionId}
            options={parseChoices(options)}
            value={typeof value === "string" ? value : null}
            onChange={(v) => onValueChange(questionId, v)}
            readOnly={readOnly}
          />
        )}
        {type === "MULTI_SUB_FIELD" && (
          <MultiSubFieldQuestion
            questionId={questionId}
            subFields={parseSubFields(options)}
            value={value && typeof value === "object" ? (value as Record<string, string>) : null}
            onChange={(v) => onValueChange(questionId, v)}
            readOnly={readOnly}
          />
        )}
        {type === "OPEN_ENDED" && (
          <OpenEndedQuestion
            questionId={questionId}
            value={typeof value === "string" ? value : null}
            onChange={(v) => onValueChange(questionId, v)}
            readOnly={readOnly}
          />
        )}
      </div>

      {/* PM Comment */}
      <div className="pl-9">
        {!showComment && !readOnly ? (
          <button
            type="button"
            onClick={() => setShowComment(true)}
            className="text-[13px] text-text-muted hover:text-accent transition-colors"
          >
            + {t("assessment.addPmComment")}
          </button>
        ) : (
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-text-muted">{t("assessment.pmComment")}</label>
            <textarea
              value={comment ?? ""}
              onChange={(e) => onCommentChange(questionId, e.target.value)}
              readOnly={readOnly}
              placeholder={t("assessment.pmCommentPlaceholder")}
              rows={2}
              className="min-h-[60px] w-full rounded-control border border-border-default bg-bg-input px-3 py-2 text-[13px] text-text-primary placeholder:text-text-muted resize-y focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent read-only:opacity-70 read-only:cursor-default"
            />
          </div>
        )}
      </div>
    </div>
  );
}
